import { cn } from "@/lib/utils";
import { Stack } from "@altimateai/lego";
import { Button, Typography } from "@altimateai/lego";
import { IconChevronDown } from "@tabler/icons-react";
import { useState } from "react";

interface DropdownProps {
  title: string;
  subtitle?: string;
  tutorial: string;
  borderColor: string;
  bgColor: string;
  stepNumber?: string;
}

function Dropdown({
  title,
  subtitle = "",
  tutorial,
  borderColor,
  bgColor,
  stepNumber,
}: DropdownProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  // Derive hover class from border class (e.g., "al-border-green-500" -> "hover:al-border-green-500")
  const hoverBorder = `hover:${borderColor}`;

  const handleDropdownClick = () => {
    setIsExpanded(p => !p);
  };

  // Add title attribute to iframe for accessibility
  const iframeTitle = `${title} tutorial`;
  const tutorialWithTitle = tutorial.replace(/<iframe /, `<iframe title="${iframeTitle}" `);

  return (
    <div
      className={cn(
        "al-rounded-xl al-border-2 al-bg-white/70 al-transition-all al-duration-200",
        isExpanded ? borderColor : `al-border-gray-300 ${hoverBorder}`
      )}
    >
      <Button
        variant="ghost"
        className="al-w-full al-h-auto al-py-4 al-px-4 al-text-left al-rounded-xl hover:al-bg-transparent"
        onClick={handleDropdownClick}
        aria-label={title}
        aria-expanded={isExpanded}
      >
        <Stack direction="row" align="center" justify="between" className="al-w-full">
          <Stack direction="row" align="center" gap="md" className="al-flex-1 al-min-w-0">
            {stepNumber && (
              <Stack
                direction="row"
                align="center"
                justify="center"
                className={cn(
                  "al-w-9 al-h-9 al-mr-2 al-rounded-full al-shrink-0 al-font-semibold al-text-white al-text-base",
                  bgColor
                )}
              >
                {stepNumber}
              </Stack>
            )}
            <Stack direction="column" className="al-flex-1 al-min-w-0">
              <Typography
                variant="section-heading"
                className="al-text-gray-900 al-leading-snug al-whitespace-normal al-break-words al-w-full"
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  size="sm"
                  className="al-text-gray-600 al-whitespace-normal al-break-words"
                >
                  {subtitle}
                </Typography>
              )}
            </Stack>
          </Stack>
          <IconChevronDown
            size={24}
            className={cn(
              "al-shrink-0 al-ml-4 al-transition-transform al-duration-300 al-text-gray-500",
              isExpanded && "al-rotate-180"
            )}
            aria-hidden="true"
          />
        </Stack>
      </Button>
      {isExpanded && (
        <div
          className="al-mx-4 al-mb-4 al-pt-4 al-border-t al-border-gray-200"
          dangerouslySetInnerHTML={{ __html: tutorialWithTitle }}
        />
      )}
    </div>
  );
}

export default Dropdown;
